import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { APP_KOPF_ABSTAND_PX } from '../editor/VertragEditor';

const LUFT_PX = 4;
export const LEISTEN_ABSTAND_PX = 12;
export const MINDEST_SCHREIBFLAECHE_PX = 96;
const APP_LEISTEN = '.MuiAppBar-positionFixed';
/**
 * RITA-1599 (Entscheidung Julia 03.11.2026): Der Kasten „Platzhalterwerte“ bleibt beim Scrollen über der
 * Werkzeugleiste stehen – ab etwa 1024 × 700 px Fenstergröße (Breite × Höhe; Referenz 1280 × 720). In kleineren
 * Fenstern scrollt er wie bisher weg, weil dort Werkzeugleiste oder kompakte Platzhalterleiste innen blättern
 * müssten (gemessen 03.11.2026: 1280 × 600 – keine Chipreihe mehr unter dem Budget; 900 px breit – Kasten 142 px
 * zweizeilig, Werkzeugleiste im Kopfzeilenmodus 172 px über dem Budget; 390 px – Kasten ≈ 200 px). Die Schwellen
 * sind bewusst runde Werte oberhalb der gemessenen Grenzfälle (1024 × 768 und 1280 × 720 mit Handbuch-Spalte passen).
 */
export const WERTEKASTEN_FEST_AB_FENSTERHOEHE_PX = 700;
export const WERTEKASTEN_FEST_AB_FENSTERBREITE_PX = 1024;

/**
 * Oben buendig aneinander anschliessende feste App-Leisten: der App-Kopf und im Layout
 * "horizontal" das Menue darunter. Eine nicht anschliessende Leiste zaehlt nicht mit.
 */
function appKopfbereich(leisten: HTMLElement[]) {
  const rechtecke = leisten
    .map((leiste) => leiste.getBoundingClientRect())
    .filter((rect) => rect.height > 0)
    .sort((a, b) => a.top - b.top);
  if (rechtecke.length === 0 || rechtecke[0].top > 1) return null;
  let unten = 0;
  let links = rechtecke[0].left;
  let rechts = rechtecke[0].right;
  for (let index = 0; index < rechtecke.length && rechtecke[index].top <= unten + 1; index += 1) {
    unten = Math.max(unten, rechtecke[index].bottom);
    links = Math.min(links, rechtecke[index].left);
    rechts = Math.max(rechts, rechtecke[index].right);
  }
  return { unten, links, rechts };
}

interface Masse {
  app: number;
  appLinks: number;
  appBreite: number;
  kopf: number;
  /** Kasten „Platzhalterwerte“ (RITA-1599); 0 ohne Kasten (Entwurf beim Berater). */
  wertekasten: number;
  werkzeuge: number;
  fenster: number;
  fensterBreite: number;
}

/** Ob der Kasten „Platzhalterwerte“ klebt: vorhanden und das Fenster groß genug (RITA-1599). */
function wertekastenKlebt(masse: Masse) {
  return (
    masse.wertekasten > 0 &&
    masse.fenster >= WERTEKASTEN_FEST_AB_FENSTERHOEHE_PX &&
    masse.fensterBreite >= WERTEKASTEN_FEST_AB_FENSTERBREITE_PX
  );
}

/** Höhe des Kastens, soweit er zur festen Zone zählt. */
const wertekastenBeitrag = (masse: Masse) => (wertekastenKlebt(masse) ? masse.wertekasten : 0);

/** Gemeinsame Messung fuer Titel, Werte-Kasten, Werkzeuge, seitliche Platzhalter und Schreibmarke. */
export default function useFesteVertragsleisten() {
  const kopfRef = useRef<HTMLDivElement>(null);
  const wertekastenRef = useRef<HTMLDivElement>(null);
  const werkzeugeRef = useRef<HTMLDivElement>(null);
  const [masse, setMasse] = useState<Masse>({
    app: APP_KOPF_ABSTAND_PX,
    appLinks: 0,
    appBreite: 0,
    kopf: 0,
    wertekasten: 0,
    werkzeuge: 0,
    fenster: 0,
    fensterBreite: 0,
  });
  const masseRef = useRef(masse);
  masseRef.current = masse;

  useLayoutEffect(() => {
    let frame = 0;
    let beobachter: ResizeObserver | null = null;
    const beobachtet = new Set<HTMLElement>();
    // App-Kopf und Menue sind je Layout unterschiedlich hoch und wechseln mit Fensterbreite und
    // Einstellung. Deshalb bei jeder Messung neu suchen. Kein config-global-Import: Importzyklus ueber pages.
    const messen = () => {
      const appLeisten = Array.from(document.querySelectorAll<HTMLElement>(APP_LEISTEN));
      appLeisten.forEach((leiste) => {
        if (!beobachtet.has(leiste)) {
          beobachtet.add(leiste);
          beobachter?.observe(leiste, { box: 'border-box' });
        }
      });
      beobachtet.forEach((leiste) => {
        if (!appLeisten.includes(leiste)) {
          beobachtet.delete(leiste);
          beobachter?.unobserve(leiste);
        }
      });
      const bereich = appKopfbereich(appLeisten);
      const app = bereich ? Math.max(0, bereich.unten) + LUFT_PX : APP_KOPF_ABSTAND_PX;
      const appLinks = bereich?.links ?? 0;
      const appBreite = bereich ? bereich.rechts - bereich.links : 0;
      const kopf = kopfRef.current?.getBoundingClientRect().height ?? 0;
      const wertekasten = wertekastenRef.current?.getBoundingClientRect().height ?? 0;
      const werkzeuge = werkzeugeRef.current?.getBoundingClientRect().height ?? 0;
      // Die Fensterhöhe bestimmt die Reihenzahl der kompakten Platzhalterleiste oben (RITA-1533) und mit der
      // Breite, ob der Kasten „Platzhalterwerte“ klebt (RITA-1599).
      const fenster = window.innerHeight;
      const fensterBreite = window.innerWidth;
      // Beim Drucken sind die Bedienleisten ausgeblendet. Letzte Bildschirmmasse erhalten.
      if (window.matchMedia?.('print').matches) return;
      setMasse((alt) =>
        alt.app === app &&
        alt.appLinks === appLinks &&
        alt.appBreite === appBreite &&
        alt.kopf === kopf &&
        alt.wertekasten === wertekasten &&
        alt.werkzeuge === werkzeuge &&
        alt.fenster === fenster &&
        alt.fensterBreite === fensterBreite
          ? alt
          : { app, appLinks, appBreite, kopf, wertekasten, werkzeuge, fenster, fensterBreite },
      );
    };
    // Messwerte veraendern die Leistenhoehen. Nicht innerhalb der ResizeObserver-Zustellung schreiben.
    const planen = () => {
      if (!frame)
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          messen();
        });
    };
    beobachter = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(planen);
    // Rahmenkasten beobachten: Eine Bildlaufleiste, die bei begrenzter Höhe erscheint, ändert die äußere Höhe,
    // ohne dass sich der Inhaltskasten ändert – die Leiste darunter stünde sonst auf einer veralteten Kante.
    [kopfRef.current, wertekastenRef.current, werkzeugeRef.current].forEach((element) => {
      if (element) beobachter?.observe(element, { box: 'border-box' });
    });
    messen();
    window.addEventListener('resize', messen);
    window.addEventListener('afterprint', messen);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      beobachter?.disconnect();
      window.removeEventListener('resize', messen);
      window.removeEventListener('afterprint', messen);
    };
  }, []);

  /**
   * Die oben klebende Platzhalterleiste trägt sich hier ein, solange sie oben steht (RITA-1533): Ihr unterer
   * Rand ist dann der Schreibrand. Live gelesen, kein Zustand: der Rückruf bleibt stabil und braucht keine
   * Nachmessung; im Druck ist die Leiste ausgeblendet (Höhe 0) und zählt nicht.
   */
  const obereLeisteRef = useRef<HTMLElement | null>(null);
  // Steht die Platzhalterleiste oben, ist sie die dritte feste Schicht: Das Höhenbudget wird dann auf drei Leisten
  // verteilt, damit unter allen dreien die Mindest-Schreibfläche bleibt (Kollegenreview !765).
  const [obereLeiste, setObereLeiste] = useState(false);
  const obereLeisteMelden = useCallback((element: HTMLElement | null) => {
    obereLeisteRef.current = element;
    setObereLeiste(element !== null);
  }, []);
  const scrollRandOben = useCallback(() => {
    const aktuell = masseRef.current;
    const basis = aktuell.app + aktuell.kopf + wertekastenBeitrag(aktuell) + aktuell.werkzeuge + LUFT_PX;
    const leiste = obereLeisteRef.current?.getBoundingClientRect();
    if (!leiste || leiste.height <= 0) return basis;
    return Math.max(basis, leiste.bottom + LUFT_PX);
  }, []);

  const wertekastenFest = wertekastenKlebt(masse);
  const wertekasten = wertekastenBeitrag(masse);
  // Alle festen Leisten koennen umbrechen. Auch dann bleibt darunter Platz zum Schreiben; mit Platzhalterleiste
  // oben teilen sich drei Schichten das Budget.
  const schichten = obereLeiste ? 3 : 2;
  const maxLeistenHoehe = `max(40px, calc((100vh - ${masse.app}px - ${MINDEST_SCHREIBFLAECHE_PX}px) / ${schichten}))`;
  // RITA-1599: Klebt der Kasten „Platzhalterwerte“, gehen seine Höhe und die GEMESSENE Titelhöhe (höchstens ihr
  // Budget) vom Budget ab; den Rest teilen sich Werkzeugleiste und Platzhalterleiste. Ein gleicher Anteil für vier
  // Schichten ließe die Werkzeugleiste im Kopfzeilenmodus bei 1280 × 720 innen blättern (157 px gegen (720 − 96 − 96) / 4 = 132 px).
  // Der Kasten selbst behält das einfache Budget – eine vom eigenen Maß abhängige Grenze schwänge sich auf.
  const maxUntereLeistenHoehe = wertekastenFest
    ? `max(40px, calc((100vh - ${masse.app}px - ${MINDEST_SCHREIBFLAECHE_PX}px - ${wertekasten}px - min(${masse.kopf}px, ${maxLeistenHoehe})) / ${schichten - 1}))`
    : maxLeistenHoehe;

  return {
    kopfRef,
    wertekastenRef,
    werkzeugeRef,
    appAbstand: masse.app,
    appLinks: masse.appLinks,
    appBreite: masse.appBreite,
    wertekastenOben: masse.app + masse.kopf,
    wertekastenFest,
    werkzeugeOben: masse.app + masse.kopf + wertekasten,
    platzhalterOben: masse.app + masse.kopf + wertekasten + masse.werkzeuge + LEISTEN_ABSTAND_PX,
    maxLeistenHoehe,
    maxUntereLeistenHoehe,
    fensterHoehe: masse.fenster,
    obereLeisteMelden,
    scrollRandOben,
  };
}

export type FesteVertragsleisten = ReturnType<typeof useFesteVertragsleisten>;
